
import React, { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Megaphone } from 'lucide-react';
import { ChatModalProps } from '@/types/chat';
import { useChat } from '@/hooks/useChat';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useToast } from '@/hooks/use-toast';

export function ChatModal({ 
  isOpen, 
  onClose, 
  bookingId, 
  groupEventId,
  otherParticipantName,
  currentUserType
}: ChatModalProps) {
  const { user } = useAuth();
  const { messages, isLoading, isSending, sendMessage, conversation } = useChat({
    bookingId,
    groupEventId,
    userType: currentUserType,
  });
  const { refreshUnreadCounts } = useUnreadMessages({
    userId: user?.id,
    userType: currentUserType
  });
  const markingAsRead = useRef(false);

  const identifier = bookingId || groupEventId;
  const isGroupEvent = !!groupEventId && !bookingId;
  // In broadcast mode: only practitioner can send, clients are read-only
  const isBroadcastMode = isGroupEvent;
  const canSend = !isBroadcastMode || currentUserType === 'practitioner';

  // Single conversation-based read marker used by both booking and group chats.
  const markConversationRead = useCallback(async () => {
    if (!conversation?.id || markingAsRead.current) return;
    try {
      markingAsRead.current = true;
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversation.id)
        .eq('is_read', false)
        .neq('sender_type', currentUserType);
      refreshUnreadCounts();
      [500, 2000].forEach(delay => setTimeout(() => refreshUnreadCounts(), delay));
    } catch (error) {
      console.error('ChatModal: Error marking as read:', error);
    } finally {
      markingAsRead.current = false;
    }
  }, [conversation?.id, currentUserType, refreshUnreadCounts]);

  useEffect(() => {
    if (!isOpen || !identifier || !conversation?.id) return;
    markConversationRead();
    const interval = setInterval(markConversationRead, 15000);
    return () => clearInterval(interval);
  }, [isOpen, identifier, conversation?.id, markConversationRead]);

  const handleClose = () => {
    markConversationRead();
    [200, 1500].forEach(delay => setTimeout(() => refreshUnreadCounts(), delay));
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md h-[500px] flex flex-col p-0">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            {isBroadcastMode && <Megaphone className="h-4 w-4 text-muted-foreground" />}
            {isBroadcastMode 
              ? (currentUserType === 'practitioner' ? 'Announcements' : `Updates from ${otherParticipantName}`)
              : `Chat with ${otherParticipantName}`
            }
          </DialogTitle>
          <DialogDescription>
            {isBroadcastMode 
              ? (currentUserType === 'practitioner' 
                  ? 'Post updates visible to all registered participants' 
                  : 'Announcements from the practitioner')
              : (groupEventId ? 'Group session conversation' : 'Appointment conversation')
            }
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          </div>
        ) : (
          <>
            <MessageList 
              messages={messages} 
              otherParticipantName={otherParticipantName}
            />
            {canSend ? (
              <MessageInput 
                onSendMessage={sendMessage} 
                isSending={isSending}
                conversationId={conversation?.id}
              />
            ) : (
              <div className="border-t p-3 text-center text-sm text-muted-foreground bg-muted/30">
                Announcements are posted by the practitioner
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
